import { useMemo, useState } from "react";

import {
  useCategoryActivation,
  useCategoryDetail,
  useCategoryList,
  useCategorySchemaMigrationRun,
  useCategorySchemaMigrations,
} from "~/features/admin/categories/hooks";
import { toCategoryDetailUI } from "~/features/admin/categories/utils/transformers";
import { PipelineDryRunResultDialog } from "~/features/dataImportProjects/components/PipelineDryRunResultDialog";
import { CategoryActivationDialog } from "./CategoryActivationDialog";
import { CategoryDetailPresenter } from "./CategoryDetailPresenter";
import { CategoryFormDialog } from "./CategoryFormDialog";
import { CategorySchemaMigrationJobLogDialog } from "./CategorySchemaMigrationJobLogDialog";
import { CategorySchemaMigrationRunDialog } from "./CategorySchemaMigrationRunDialog";

interface CategoryDetailProps {
  id: string;
  /** Optional override for the back button (e.g., close-the-sheet vs. navigate to list). */
  onBack: () => void;
}

/**
 * Reusable from both the full-page route and the Peek side panel.
 * Owns data, dialog state, and activation flow; renders the presenter.
 */
export function CategoryDetail({ id, onBack }: CategoryDetailProps) {
  const { data, effectiveSchemasData } = useCategoryDetail(id);
  const { data: categoryListData } = useCategoryList();
  const schemaMigrations = useCategorySchemaMigrations(id);
  const schemaMigrationRun = useCategorySchemaMigrationRun({ onRefresh: schemaMigrations.refresh });
  const { onActivate, onDeactivate, processing } = useCategoryActivation();
  const [pendingAction, setPendingAction] = useState<"activate" | "deactivate" | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [pendingMigrationRun, setPendingMigrationRun] = useState<{
    planKey: string;
    jobCount: number;
  } | null>(null);

  const raw = data.categories[0];
  // effectiveSchemasData is undefined only when the query is skipped, which is exactly
  // when `raw` is absent too, so the transform never runs with a partial pair.
  const category = useMemo(
    () =>
      raw && effectiveSchemasData
        ? toCategoryDetailUI(
            raw,
            effectiveSchemasData.cnutListEffectiveCategoryPropertySchemas,
            categoryListData.categories,
          )
        : null,
    [raw, effectiveSchemasData, categoryListData.categories],
  );

  if (!category) {
    return (
      <div className="rounded-[var(--radius-md)] border border-border-default bg-white px-5 py-12 text-center text-14-regular text-fg-2">
        —
      </div>
    );
  }

  const handleConfirm = async () => {
    if (pendingAction === "activate") {
      await onActivate({ id: category.id });
    } else if (pendingAction === "deactivate") {
      await onDeactivate({ id: category.id });
    }
    setPendingAction(null);
  };

  const handleMigrationRunConfirm = (autoApprove: boolean) => {
    if (!pendingMigrationRun) return;
    const { planKey } = pendingMigrationRun;
    setPendingMigrationRun(null);
    void schemaMigrationRun.run("run", planKey, { autoApprove });
  };

  return (
    <>
      <CategoryDetailPresenter
        category={category}
        onBack={onBack}
        onEdit={() => setEditOpen(true)}
        onActivate={() => setPendingAction("activate")}
        onDeactivate={() => setPendingAction("deactivate")}
        processing={processing}
        schemaMigrations={schemaMigrations.migrations}
        schemaMigrationsLoading={schemaMigrations.loading}
        schemaMigrationsError={schemaMigrations.error}
        schemaMigrationsHasNextPage={schemaMigrations.hasNextPage}
        schemaMigrationsLoadingMore={schemaMigrations.loadingMore}
        onLoadMoreSchemaMigrations={schemaMigrations.loadMore}
        onRunSchemaMigration={(action, planKey, jobCount) => {
          if (action === "dryrun") {
            void schemaMigrationRun.run(action, planKey);
            return;
          }
          setPendingMigrationRun({ planKey, jobCount });
        }}
        schemaMigrationDryRunResults={schemaMigrationRun.dryRunResults}
        onOpenSchemaMigrationDryRunResult={schemaMigrationRun.openDryRunResult}
        runningSchemaMigrationPlanKey={schemaMigrationRun.runningPlanKey}
        runningSchemaMigrationAction={schemaMigrationRun.runningAction}
      />
      <CategoryActivationDialog
        open={pendingAction !== null}
        action={pendingAction ?? "deactivate"}
        name={category.name}
        processing={processing}
        onConfirm={handleConfirm}
        onCancel={() => setPendingAction(null)}
      />
      <CategoryFormDialog open={editOpen} onOpenChange={setEditOpen} categoryId={category.id} />
      <CategorySchemaMigrationRunDialog
        open={pendingMigrationRun !== null}
        planKey={pendingMigrationRun?.planKey ?? ""}
        jobCount={pendingMigrationRun?.jobCount ?? 0}
        processing={schemaMigrationRun.runningPlanKey !== null}
        onConfirm={handleMigrationRunConfirm}
        onCancel={() => setPendingMigrationRun(null)}
      />
      <CategorySchemaMigrationJobLogDialog
        jobRun={schemaMigrationRun.jobRun}
        onClose={schemaMigrationRun.clearJobRun}
      />
      <PipelineDryRunResultDialog
        open={schemaMigrationRun.dryRunResult !== null}
        onOpenChange={(open) => !open && schemaMigrationRun.clearDryRunResult()}
        target={schemaMigrationRun.dryRunResult}
        jobLabels={new Map()}
      />
    </>
  );
}
